import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  BarChart3, 
  Download, 
  RefreshCw,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Languages,
  FileX,
  FileWarning,
  TrendingUp,
  Eye,
  Filter,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const COLORS = ['#10B981', '#EF4444', '#F59E0B', '#6366F1', '#EC4899'];

export default function Reports() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  
  // Documents with issues
  const [documentsWithIssues, setDocumentsWithIssues] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [showDocsWithIssues, setShowDocsWithIssues] = useState(false);
  
  // Validation rules for filtering
  const [validationRules, setValidationRules] = useState([]);
  const [selectedRuleIds, setSelectedRuleIds] = useState([]);
  const [selectedIssueTypes, setSelectedIssueTypes] = useState([]);
  
  const issueTypes = [
    { id: 'wrong_language', label: 'Wrong Language' },
    { id: 'missing_content', label: 'Missing Content' },
    { id: 'improper_format', label: 'Improper Format' },
    { id: 'wrong_content', label: 'Wrong Content' },
  ];

  useEffect(() => {
    fetchSummary();
    fetchValidationRules();
  }, []);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await api().get('/reports/summary');
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const fetchValidationRules = async () => {
    try {
      const response = await api().get('/validation-rules');
      setValidationRules(response.data);
    } catch (error) {
      console.error('Failed to load validation rules');
    }
  };

  const fetchDocumentsWithIssues = async () => {
    setLoadingDocs(true);
    try {
      const params = new URLSearchParams();
      if (selectedRuleIds.length > 0) {
        params.append('rule_ids', selectedRuleIds.join(','));
      }
      if (selectedIssueTypes.length > 0) {
        params.append('issue_types', selectedIssueTypes.join(','));
      }
      
      const response = await api().get(`/reports/documents-with-issues?${params.toString()}`);
      setDocumentsWithIssues(response.data.documents);
      setShowDocsWithIssues(true);
    } catch (error) {
      toast.error('Failed to load documents with issues');
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api().get(`/reports/export?format=${exportFormat}`, {
        responseType: exportFormat === 'csv' ? 'blob' : 'json',
      });
      
      if (exportFormat === 'csv') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'verification_report.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Report exported successfully');
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'verification_report.json');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Report exported successfully');
      }
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const toggleRuleSelection = (ruleId) => {
    setSelectedRuleIds(prev => 
      prev.includes(ruleId) 
        ? prev.filter(id => id !== ruleId)
        : [...prev, ruleId]
    );
  };

  const toggleIssueTypeSelection = (typeId) => {
    setSelectedIssueTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  const getIssueTypeBadgeClass = (type) => {
    const classes = {
      wrong_language: 'bg-amber-100 text-amber-800',
      missing_content: 'bg-red-100 text-red-800',
      improper_format: 'bg-indigo-100 text-indigo-800',
      wrong_content: 'bg-pink-100 text-pink-800',
    };
    return classes[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const issueData = summary ? [
    { name: 'Wrong Language', value: summary.wrong_language_count, color: '#F59E0B' },
    { name: 'Missing Content', value: summary.missing_content_count, color: '#EF4444' },
    { name: 'Improper Format', value: summary.improper_format_count, color: '#6366F1' },
    { name: 'Wrong Content', value: summary.wrong_content_count, color: '#EC4899' },
  ].filter(d => d.value > 0) : [];

  const statusData = summary ? [
    { name: 'Validated', value: summary.validated_documents - summary.documents_with_issues, fill: '#10B981' },
    { name: 'With Issues', value: summary.documents_with_issues, fill: '#EF4444' },
    { name: 'Pending', value: summary.total_documents - summary.validated_documents, fill: '#94A3B8' },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">Reports</h1>
          <p className="text-muted-foreground mt-1">Validation statistics and export options</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={exportFormat} onValueChange={setExportFormat}>
            <SelectTrigger className="w-[120px]" data-testid="export-format-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">CSV</SelectItem>
              <SelectItem value="json">JSON</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExport} disabled={exporting} data-testid="export-btn">
            {exporting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Summary Stats - Clickable */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => navigate('/documents')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Documents</p>
                <p className="text-2xl font-bold font-mono">{summary?.total_documents || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => navigate('/documents?status=validated')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Validated</p>
                <p className="text-2xl font-bold font-mono">{summary?.validated_documents || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:bg-muted/30 transition-colors border-red-200" 
          onClick={() => {
            fetchDocumentsWithIssues();
          }}
          data-testid="with-issues-card"
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">With Issues</p>
                <p className="text-2xl font-bold font-mono text-red-600">{summary?.documents_with_issues || 0}</p>
                <p className="text-xs text-muted-foreground">Click to view</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Avg Match</p>
                <p className="text-2xl font-bold font-mono">{summary?.average_match_percentage || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Documents with Issues Section */}
      {showDocsWithIssues && (
        <Card className="border-red-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Documents with Issues ({documentsWithIssues.length})
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowDocsWithIssues(false)}
              >
                {showDocsWithIssues ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex flex-col lg:flex-row gap-4 mb-6 p-4 bg-muted/30 rounded-lg">
              <div className="flex-1">
                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filter by Broken Rules
                </p>
                <div className="flex flex-wrap gap-2">
                  {validationRules.map(rule => (
                    <label 
                      key={rule.id} 
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                        selectedRuleIds.includes(rule.id) 
                          ? 'bg-primary text-primary-foreground border-primary' 
                          : 'bg-background hover:bg-muted'
                      }`}
                    >
                      <Checkbox 
                        checked={selectedRuleIds.includes(rule.id)}
                        onCheckedChange={() => toggleRuleSelection(rule.id)}
                        className="hidden"
                      />
                      <span className="text-xs">{rule.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium mb-2">Filter by Issue Type</p>
                <div className="flex flex-wrap gap-2">
                  {issueTypes.map(type => (
                    <label 
                      key={type.id} 
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                        selectedIssueTypes.includes(type.id) 
                          ? 'bg-primary text-primary-foreground border-primary' 
                          : 'bg-background hover:bg-muted'
                      }`}
                    >
                      <Checkbox 
                        checked={selectedIssueTypes.includes(type.id)}
                        onCheckedChange={() => toggleIssueTypeSelection(type.id)}
                        className="hidden"
                      />
                      <span className="text-xs">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-end">
                <Button onClick={fetchDocumentsWithIssues} disabled={loadingDocs}>
                  {loadingDocs ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Apply Filters'}
                </Button>
              </div>
            </div>

            {/* Documents Table */}
            {loadingDocs ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : documentsWithIssues.length > 0 ? (
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document</TableHead>
                      <TableHead>Language</TableHead>
                      <TableHead>Broken Rules</TableHead>
                      <TableHead>Issues</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documentsWithIssues.map(doc => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <Link 
                            to={`/documents/${doc.id}`}
                            className="font-medium hover:text-primary hover:underline"
                          >
                            {doc.name}
                          </Link>
                          <p className="text-xs text-muted-foreground">{doc.title}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="uppercase text-xs">
                            {doc.language}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {doc.broken_rules.map((rule, idx) => (
                              <Badge 
                                key={rule.result_id || rule.rule_id || idx} 
                                variant="destructive" 
                                className="text-xs"
                                title={rule.details?.message}
                              >
                                {rule.rule_name}
                                {rule.match_percentage !== null && ` (${rule.match_percentage}%)`}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {doc.issues.map((issue, idx) => (
                              <Badge 
                                key={issue.id || idx} 
                                className={`text-xs ${getIssueTypeBadgeClass(issue.type)}`}
                              >
                                {issue.type.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Link to={`/documents/${doc.id}`}>
                            <Button variant="ghost" size="icon">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                <p>No documents with issues found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Document Status Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Document Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Issue Breakdown Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Issue Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {issueData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={issueData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {issueData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                <div className="text-center">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                  <p>No issues found</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Issue Type Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-['Manrope']">Issue Type Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div 
              className="p-4 border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => {
                setSelectedIssueTypes(['wrong_language']);
                fetchDocumentsWithIssues();
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <Languages className="w-5 h-5 text-amber-600" />
                <span className="font-medium">Wrong Language</span>
              </div>
              <p className="text-3xl font-bold font-mono">{summary?.wrong_language_count || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">Click to view documents</p>
            </div>
            
            <div 
              className="p-4 border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => {
                setSelectedIssueTypes(['missing_content']);
                fetchDocumentsWithIssues();
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <FileX className="w-5 h-5 text-red-600" />
                <span className="font-medium">Missing Content</span>
              </div>
              <p className="text-3xl font-bold font-mono">{summary?.missing_content_count || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">Click to view documents</p>
            </div>
            
            <div 
              className="p-4 border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => {
                setSelectedIssueTypes(['improper_format']);
                fetchDocumentsWithIssues();
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <FileWarning className="w-5 h-5 text-indigo-600" />
                <span className="font-medium">Improper Format</span>
              </div>
              <p className="text-3xl font-bold font-mono">{summary?.improper_format_count || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">Click to view documents</p>
            </div>
            
            <div 
              className="p-4 border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => {
                setSelectedIssueTypes(['wrong_content']);
                fetchDocumentsWithIssues();
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-5 h-5 text-pink-600" />
                <span className="font-medium">Wrong Content</span>
              </div>
              <p className="text-3xl font-bold font-mono">{summary?.wrong_content_count || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">Click to view documents</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
